import {
    FeatureFlag,
    FeatureFlagsExistenceLogicalOperator,
} from "@common-shared/config/featureFlags.config"
import {
    OneVerifyPermission,
    PermissionsExistenceLogicalOperator,
} from "@common-shared/config/rolesPermissions.config"

import type { OrganizationAvailableTabKey } from "../types/general.types"
import type {
    IMenuItem,
    IOrganizationAvailableTab,
    IProfileAvailableTab,
} from "../types/objects.types"
import { FEATURE_FLAGS_CONFIG } from "./featureFlags.config"
import { ROUTES_CONFIG } from "./routes.config"

export const MENU_CONFIG = {
    mainMenu: [
        {
            label: "Checker",
            url: ROUTES_CONFIG.coverageCheckerUrl,
            featureFlag: FEATURE_FLAGS_CONFIG.checkerFeatureFlags,
            featureFlagExistenceOperator:
                FeatureFlagsExistenceLogicalOperator.OR,
        },
        {
            label: "Reports",
            url: ROUTES_CONFIG.reportsUrl,
            submenu: [
                {
                    label: "General",
                    url: ROUTES_CONFIG.reportsUrl,
                    neededPermissions: [
                        OneVerifyPermission.viewHistoricalCoverageChecks,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_GENERAL_REPORTS,
                },
                {
                    label: "Medicaid",
                    url: ROUTES_CONFIG.medicaidReportsUrl,
                    neededPermissions: [
                        OneVerifyPermission.viewHistoricalCoverageChecks,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_MEDICAID_REPORTS,
                },
                {
                    label: "Overrides",
                    url: ROUTES_CONFIG.overridesUrl,
                    neededPermissions: [
                        OneVerifyPermission.overrideCoverageData,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_OVERRIDES,
                },
                {
                    label: "Resets",
                    url: ROUTES_CONFIG.planYearResetsUrl,
                    neededPermissions: [OneVerifyPermission.viewPlanYearResets],
                    featureFlag: FeatureFlag.ONE_VERIFY_PLAN_YEAR_RESETS,
                },
                {
                    label: "Agentic",
                    url: ROUTES_CONFIG.agenticReportsUrl,
                    neededPermissions: [
                        OneVerifyPermission.viewHistoricalCoverageChecks,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_AGENTIC_REPORTS,
                },
                {
                    label: "Search",
                    url: ROUTES_CONFIG.searchUrl,
                    neededPermissions: [
                        OneVerifyPermission.viewHistoricalCoverageChecks,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_SEARCH,
                },
            ],
        },

        {
            label: "Workflows",
            url: ROUTES_CONFIG.workflowManagementUrl,
            neededPermissions: [
                OneVerifyPermission.manageTaskAssignments,
                OneVerifyPermission.workTaskAssignments,
                OneVerifyPermission.manageBillerGroups,
            ],
            featureFlag: [
                FeatureFlag.ONE_VERIFY_TASKS,
                FeatureFlag.ONE_VERIFY_BILLER_GROUPS,
            ],
            submenu: [
                {
                    label: "Policy Assignment",
                    url: ROUTES_CONFIG.taskAssignmentsUrl,
                    neededPermissions: [
                        OneVerifyPermission.manageTaskAssignments,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_TASKS,
                    // Shows if: user has manageTaskAssignments OR (user has workTaskAssignments AND ONE_VERIFY_BILLER_SELF_ASSIGN flag is enabled)
                    combinedPermissions: [
                        {
                            neededPermissions: [
                                OneVerifyPermission.workTaskAssignments,
                            ],
                            featureFlag:
                                FeatureFlag.ONE_VERIFY_BILLER_SELF_ASSIGN,
                        },
                    ],
                },
                {
                    label: "Assigned Policies",
                    url: ROUTES_CONFIG.assignedTasksUrl,
                    neededPermissions: [
                        OneVerifyPermission.workTaskAssignments,
                    ],
                    featureFlag: FeatureFlag.ONE_VERIFY_TASKS,
                },
                {
                    label: "Manage Groups",
                    url: ROUTES_CONFIG.manageGroupsUrl,
                    neededPermissions: [OneVerifyPermission.manageBillerGroups],
                    featureFlag: FeatureFlag.ONE_VERIFY_BILLER_GROUPS,
                },
            ],
        },

        {
            label: "Insights",
            url: ROUTES_CONFIG.insightsUrl,
            neededPermissions: [OneVerifyPermission.viewPracticeInsights],
        },

        {
            label: "Integrations",
            url: ROUTES_CONFIG.integrationsUrl,
            neededPermissions: [OneVerifyPermission.managePracticeIntegrations],
            featureFlag: FeatureFlag.ONE_VERIFY_INTEGRATIONS,
        },
    ] as IMenuItem[],

    accountMenu: [
        {
            label: "Account",
            url: ROUTES_CONFIG.profileUrl,
        },
        {
            label: "Organization",
            url: ROUTES_CONFIG.organizationUrl,
        },
        {
            label: "Network Status",
            url: ROUTES_CONFIG.networkStatusRulesUrl,
            neededPermissions: [OneVerifyPermission.manageNetworkStatusRules],
            featureFlag: FeatureFlag.ONE_VERIFY_NETWORK_STATUS_RULES,
        },
        {
            label: "Support",
            url: ROUTES_CONFIG.supportUrl,
        },
        {
            label: "Log out",
            url: ROUTES_CONFIG.logoutUrl,
        },
    ] as IMenuItem[],

    profileAvailableTabs: [
        {
            key: "account",
            label: "Account Details",
        },
    ] as IProfileAvailableTab[],

    organizationTabsHiddenKeys: [] as OrganizationAvailableTabKey[],

    organizationAvailableTabs: [
        {
            key: "information",
            label: "Organization",
        },
        {
            key: "users",
            label: "Users",
            neededPermissions: [OneVerifyPermission.managePracticeUsers],
        },
        {
            key: "payers",
            label: "Payer Rates",
            neededPermissions: [
                OneVerifyPermission.readPayersConfiguration,
                OneVerifyPermission.managePayersConfiguration,
            ],
            neededPermissionsLogicalOperator:
                PermissionsExistenceLogicalOperator.OR,
        },
    ] as IOrganizationAvailableTab[],
}
